import { ReactNode } from 'react';

import { useField } from 'react-final-form';

import { MultipleFileInput as Input } from '@vk-hr-tek/ui/input';

import { ValidateType, composeValidators } from '../validation';

/**
 * Свойства компонента {@link MultipleFileInput}
 */
interface MultipleFileInputProps {
  /**
   * Метка поля ввода.
   */
  label: string;

  /**
   * Иконка для метки.
   */
  labelIcon?: ReactNode;

  /**
   * Всплывающая подсказка.
   */
  tooltip?: ReactNode;

  /**
   * Обязательность заполнения поля.
   *
   * @defaultValue `false`
   */
  required?: boolean;

  /**
   * Состояние отключения поля.
   *
   * @defaultValue `false`
   */
  disabled?: boolean;

  /**
   * Имя поля ввода, используемое в форме.
   */
  name: string;

  /**
   * Массив валидаторов для поля.
   */
  validate?: ValidateType[];

  /**
   * Описание поля.
   */
  description?: string;

  /**
   * Текст для дропзоны.
   */
  dropzoneText?: string;

  /**
   * Флаг авторизации.
   */
  withAuth?: boolean;

  /**
   * Функция для кэширования файла.
   */
  cache?: (file: File) => Promise<{ file_id: string; expired_at: string }>;

  /**
   * Максимальное количество документов, доступное для подкрепления.
   */
  maxLength?: number;

  /**
   * Показывать ошибку без взаимодействия с полем.
   *
   * @defaultValue `false`
   */
  validationWithoutTouch?: boolean;

  /**
   * Показывать ошибку если поле задизейблено.
   *
   * @defaultValue `false`
   */
  validationWhenDisabled?: boolean;

  /**
   * Флаг ошибки.
   */
  error?: boolean;

  /**
   * Текст ошибки.
   */
  errorText?: string;

  /**
   * Показывать счётчик с порядковым номером у превью файлов.
   *
   * @defaultValue `true`
   */
  withCounter?: boolean;
}

/**
 * Компонент для ввода нескольких файлов с возможностью валидации и кэширования.
 *
 * @public
 * @param label - Метка поля ввода.
 * @param labelIcon - Иконка для метки.
 * @param tooltip - Всплывающая подсказка.
 * @param required - Обязательность заполнения поля.
 * @param name - Имя поля ввода, используемое в форме.
 * @param validate - Массив валидаторов для поля.
 * @param disabled - Состояние отключения поля.
 * @param description - Описание поля.
 * @param dropzoneText - Текст для дропзоны.
 * @param withAuth - Флаг авторизации.
 * @param cache - Функция для кэширования файла.
 * @param maxLength - Максимальное количество документов, доступное для подкрепления.
 * @param validationWithoutTouch - Показывать ошибку без взаимодействия с полем.
 * @param validationWhenDisabled - Показывать ошибку если поле задизейблено.
 * @param error - Флаг ошибки.
 * @param errorText - Текст ошибки.
 * @param withCounter - Показывать счётчик с порядковым номером у превью файлов.
 * @returns Компонент для ввода нескольких файлов.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link MultipleFileInputProps}
 */
export const MultipleFileInput = ({
  label,
  labelIcon,
  tooltip,
  required,
  name,
  validate,
  disabled = false,
  description,
  dropzoneText,
  withAuth,
  cache,
  maxLength,
  validationWithoutTouch = false,
  validationWhenDisabled = false,
  error: errorOverride,
  errorText,
  withCounter,
}: MultipleFileInputProps) => {
  const validators = composeValidators(validate || []);

  const { input, meta } = useField(name, {
    validate: validators,
  });

  const { onChange, onBlur } = input;

  const errorDisabled = disabled || !validationWhenDisabled;

  const renderError =
    !errorDisabled && validationWithoutTouch
      ? !!input.value && !!meta.error
      : meta.touched && !!meta.error;

  return (
    <Input
      {...input}
      value={input.value}
      label={label}
      labelIcon={labelIcon}
      disabled={disabled}
      required={required}
      tooltip={tooltip}
      onChange={onChange}
      onBlur={onBlur}
      cache={cache}
      validate={validators}
      error={errorOverride || renderError}
      helperText={errorText || (renderError && meta.error)}
      description={description}
      dropzoneText={dropzoneText}
      maxLength={maxLength}
      withAuth={withAuth}
      withCounter={withCounter}
    />
  );
};
